import {
  isInternalStepId,
  StepConfigCommon,
  TriggerStepCreate,
} from "@dots-projects/types";
import { useForm, Controller } from "react-hook-form";
import produce from "immer";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import invariant from "ts-invariant";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import { FormLabel } from "./FormLabel";
import { Input } from "./Input";
import { MemberSelector } from "../common";
import {
  addSteps,
  deleteSteps,
  saveDraft,
  updateStep,
} from "./reducer/actionCreators";
import { InputNumber, Checkbox, Tooltip } from "antd";
import { createStepsFromTemplate, STEP_TEMPLATES } from "./stepTemplates";
import { InfoCircleFilled } from "@ant-design/icons";
/**
 * Renders the form for a "Create Channel" step. When the form is submitted, generate the step data and publish an update action to add a new step to the trigger flow.
 */
export function StepFormCreateChannel({ step }: { step: TriggerStepCreate }) {
  invariant(step.type === "create_channel");

  const channelNameSuffix = "{{username}}";

  const defaultValues = {
    name: step.name,
    channelName: step.configs.channelName.replace("-{{user.username}}", ""),
    channelType: step.configs.channelType,
    userIds: step.configs.userIds,
    deleteAfterMinutes: step.configs.deleteAfterMinutes,
    deleteChannel: undefined !== step.configs.deleteAfterMinutes,
    channelNameSuffix,
  };

  const DELETE_STEP_MINUTES_DEFAULT = 10;

  const form = useForm({
    defaultValues,
  });
  const { handleSubmit, register, control, watch, setValue } = form;
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const readOnly = !state.trigger.current.isDraft;
  const deleteChannelActive = watch("deleteChannel");

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      stepDraft.configs.channelName = `${data.channelName}-{{user.username}}`;
      stepDraft.configs.channelType = data.channelType;
      stepDraft.configs.userIds = data.userIds;

      const currentTransitionsIds = step.transitions.map((t) => t.targetStepId);
      const deleteInternalStep = state.trigger.current.steps.find((s) => {
        return (
          currentTransitionsIds.includes(s.id) &&
          s.type === "delete_channel" &&
          isInternalStepId(s.id)
        );
      });

      if (data.deleteChannel) {
        // It's possible that the user checked the delete channel checkbox but does not change the minutes in that case
        // deleteAfterMinutes will be undefined, so we set it to the default value.
        stepDraft.configs.deleteAfterMinutes =
          data.deleteAfterMinutes || DELETE_STEP_MINUTES_DEFAULT;

        if (!deleteInternalStep) {
          const internalDeleteChannelTemplate = STEP_TEMPLATES.find(
            ({ title }) => title === "Internal delete channel"
          )!;

          const finalSteps = createStepsFromTemplate(
            internalDeleteChannelTemplate,
            state.trigger.current,
            step.id
          );

          const deleteInternalStep = finalSteps.find(
            (step) => step.type === "delete_channel"
          )!;

          const deleteInternalStepUpdated = produce(
            deleteInternalStep,
            (deleteInternalStep) => {
              (deleteInternalStep.configs as StepConfigCommon).delaySeconds =
                stepDraft.configs.deleteAfterMinutes! * 60;
            }
          );

          dispatch(
            addSteps([deleteInternalStepUpdated], {
              parentStepId: step.id,
            })
          );
          /**
           * We are managing transitions here because dispatch(addSteps) adds a transition
           * between create_channel and the internal delete_channel step
           * but the issue is when we execute dispatch(updateStep(newStep))
           * the transition added is removed because stepDraft is out of sync with the state.
           * See https://dots-community.slack.com/archives/C017KSS20DS/p1669413214107199
           */
          stepDraft.transitions.push({
            targetStepId: deleteInternalStepUpdated.id,
          });
        } else {
          // If we have a deleted channel step, we update delaySeconds property
          const deleteInternalStepUpdated = produce(
            deleteInternalStep,
            (deleteInternalStep) => {
              (deleteInternalStep.configs as StepConfigCommon).delaySeconds =
                stepDraft.configs.deleteAfterMinutes! * 60;
            }
          );
          dispatch(updateStep(deleteInternalStepUpdated));
        } // If the user unchecks the delete channel checkbox, we delete the delete channel step
      } else if (!data.deleteChannel && deleteInternalStep) {
        stepDraft.configs.deleteAfterMinutes = undefined;
        /**
         * We are managing transitions here because dispatch(deleteSteps([deleteInternalStep.id])) deletes the transition
         * between create_channel and the internal delete_channel step
         * but the issue is when we execute dispatch(updateStep(newStep))
         * the transition removed is added again because stepDraft is out of sync with the state
         * See https://dots-community.slack.com/archives/C017KSS20DS/p1669413214107199
         */
        dispatch(deleteSteps([deleteInternalStep.id]));
        stepDraft.transitions = stepDraft.transitions.filter(
          (t) => t.targetStepId !== deleteInternalStep?.id
        );
      }
    });

    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      step={step}
    >
      <Controller
        control={control}
        name="deleteChannel"
        render={({ field }) => (
          <div className="my-2">
            <Checkbox
              disabled={readOnly}
              checked={field.value}
              onChange={field.onChange}
            >
              <span className="font-bold">
                Delete channel regardless of user finishing the flow
              </span>{" "}
              <span>
                <Tooltip title="If you don't select this, then you will need to add a Delete channel step for it to delete when the flow ends. We do auto delete oldest channels in the event we exceed channel limit. As long as you didn't delete the two categories that Dots auto created, we can at the most have 100 channels in your server. Please reach out to us with any questions!">
                  <InfoCircleFilled />
                </Tooltip>
              </span>
            </Checkbox>
          </div>
        )}
      />
      {deleteChannelActive && (
        <FormLabel label="Delete after (in minutes)">
          <Controller
            control={control}
            name="deleteAfterMinutes"
            render={({ field }) => (
              <>
                <InputNumber
                  readOnly={readOnly}
                  min={1}
                  max={2000}
                  defaultValue={field.value || DELETE_STEP_MINUTES_DEFAULT}
                  onChange={field.onChange}
                />
              </>
            )}
          />
        </FormLabel>
      )}
      <FormLabel label="Channel name">
        <div className={"flex items-center"}>
          {/*I'm using inline styles here because tailwind 2.2.17 doesn't have a flex-basis class or if it exists I could not find it!*/}
          <div style={{ flexBasis: "55%" }}>
            <Controller
              control={control}
              name="channelName"
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  readOnly={readOnly}
                  className={"w-full"}
                  onBlur={onBlur}
                  value={value}
                  onChange={(e) => {
                    // Replace spaces with dashes
                    const str = e.target.value.replace(/\s+/g, "-");
                    e.target.value = str;
                    onChange(e);
                  }}
                />
              )}
            />
          </div>
          <div>-</div>
          {/*I'm using inline styles here because tailwind 2.2.17 doesn't have a flex-basis class or if it exists I could not find it!*/}
          <div style={{ flexBasis: "40%" }}>
            <Input
              readOnly={true}
              disabled={true}
              className={"w-full"}
              {...register("channelNameSuffix")}
            />
          </div>
        </div>
      </FormLabel>

      <FormLabel label="Category type">
        <select
          {...register("channelType")}
          style={{ width: "100%" }}
          disabled={readOnly}
        >
          <option value={"onboarding"}>Onboarding</option>
        </select>
      </FormLabel>
      <FormLabel label="Who to include">
        <Controller
          control={control}
          name="userIds"
          render={({ field: { onChange } }) => (
            <MemberSelector
              onChange={onChange}
              readOnly={readOnly}
              preSelectedMembersIds={defaultValues.userIds}
            />
          )}
        />
      </FormLabel>
    </StepFormWithCommonParts>
  );
}
